/* eslint-disable max-lines-per-function */
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import {
   PaymentParameters,
   VisitAlreadySettledFault,
   VisitMedicalService,
   VisitSettlement,
   VisitSettlementQuery,
   VisitSettlementUpdateMedicalServicesCommand,
   VisitSettlementUpdatePaymentCommand,
} from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';
import {
   ExternalSystemBusinessFault,
   FiscalDocumentProviderAuthorizationFault,
   ReceiptCreateCommand,
} from '@dreryk/egabinet-invoice-frontend-api';
import { TaxIdentifier } from '@dreryk/tax-identifier-common-api';

import { refetchCqrsQuery, useCqrsCommand } from '@dreryk/command-query-react-common';
import { convertDateToLocalDate, createInterpolatedTranslationKey } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { PaymentFormBean, PaymentMethod } from '../shared/form/payment/payment.types';
import { mapPaymentMethodToApi, paymentAmountToMoney } from '../shared/form/payment/payment.utils';
import {
   mapVisitMedicalServiceToFiscalItems,
   medicalServicesToVisitMedicalServiceParameters,
} from '../shared/medicalServices/medicalServices.utils';
import { performVisitSettlementCqrsFind } from '../shared/performVisitSettlementCqrsFind';
import { useRefetchVisitSettlement } from '../shared/useRefetchVisitSettlement';
import { visitSettlementTranslationKeys } from '../translations';
import { VisitSettlementBean } from '../visitSettlement.types';

import { mapFormBeanPaymentMethodToApiPaymentMethod } from './VisitSettlementPaymentModal.utils';

type VisitSettlementUpdatePaymentActionRenderProps = {
   updatePayment: (paymentFormData: PaymentFormBean) => Promise<void>;
};

type VisitSettlementUpdatePaymentActionProps = {
   children: (renderProps: VisitSettlementUpdatePaymentActionRenderProps) => JSX.Element;
   appointmentId: number;
   onVisitAlreadySettledFault?: VoidFunction;
};

export const VisitSettlementUpdatePaymentAction = (props: VisitSettlementUpdatePaymentActionProps): JSX.Element => {
   const refetchVisitSettlement = useRefetchVisitSettlement();

   const { executeAsync: updatePayment } = useCqrsCommand<void, PaymentFormBean, VisitAlreadySettledFault>(
      (parameters) => {
         const command = new VisitSettlementUpdatePaymentCommand();
         command.purchaseId = PurchaseId.forAppointment(AppointmentId.of(props.appointmentId));

         if (parameters.amount) {
            const payment: PaymentParameters = {
               paymentAmount: paymentAmountToMoney(parameters.amount),
               paymentMethod: mapPaymentMethodToApi(parameters.method),
               paymentMethodDescription: Choice.withUseDefaultIfNull(parameters.methodDescription ?? null),
            };

            command.payment = Choice.of(payment);
         }

         return command;
      },
      {
         onSuccess: () => {
            refetchVisitSettlement();
            dispatchSnackbarPositiveEvent(
               visitSettlementTranslationKeys.paymentModal.commands.updatePayment.successfully
            );
         },
         onFault: {
            'egabinet-financial-settlement-frontend-api:VisitAlreadySettledFault': () => {
               refetchVisitSettlement();
               dispatchSnackbarNegativeEvent(
                  visitSettlementTranslationKeys.paymentModal.commands.updatePayment.errors.visitAlreadySettledFault,
                  {
                     autoHideDelayInMs: false,
                  }
               );
               props.onVisitAlreadySettledFault?.();
            },
         },
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(
               visitSettlementTranslationKeys.paymentModal.commands.updatePayment.errors.unexpectedError
            );
         },
      }
   );

   return props.children({
      updatePayment: async (formData) => {
         await updatePayment(formData);
      },
   });
};

type VisitSettlementUpdateMedicalServicesActionRenderProps = {
   updateVisitSettlement: (visitSettlement: VisitSettlementBean) => Promise<void>;
};

type VisitSettlementUpdateMedicalServicesActionProps = {
   children: (renderProps: VisitSettlementUpdateMedicalServicesActionRenderProps) => JSX.Element;
   appointmentId: number;
   silent: boolean;
   onVisitAlreadySettledFault?: VoidFunction;
};

export const VisitSettlementUpdateMedicalServicesAction = (
   props: VisitSettlementUpdateMedicalServicesActionProps
): JSX.Element => {
   const refetchVisitSettlement = useRefetchVisitSettlement();

   const { executeAsync: updateVisitSettlement } = useCqrsCommand<void, VisitSettlementBean, VisitAlreadySettledFault>(
      (parameters) => {
         const command = new VisitSettlementUpdateMedicalServicesCommand();
         command.purchaseId = PurchaseId.forAppointment(AppointmentId.of(props.appointmentId));
         command.priceChangeReason = Choice.withUseDefaultIfNull(parameters.priceChangeReason);
         command.medicalServiceParameters = medicalServicesToVisitMedicalServiceParameters(parameters.medicalServices);
         return command;
      },
      {
         onSuccess: () => {
            refetchVisitSettlement();
            if (!props.silent) {
               dispatchSnackbarPositiveEvent(
                  visitSettlementTranslationKeys.paymentModal.commands.updateMedicalServices.successfully
               );
            }
         },
         onFault: {
            'egabinet-financial-settlement-frontend-api:VisitAlreadySettledFault': () => {
               refetchVisitSettlement();
               dispatchSnackbarNegativeEvent(
                  visitSettlementTranslationKeys.paymentModal.commands.updateMedicalServices.errors
                     .visitAlreadySettledFault,
                  {
                     autoHideDelayInMs: false,
                  }
               );
               props.onVisitAlreadySettledFault?.();
            },
         },
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(
               visitSettlementTranslationKeys.paymentModal.commands.updateMedicalServices.errors.unexpectedError
                  .blockingOperation
            );
         },
      }
   );

   return props.children({
      updateVisitSettlement: async (formData) => {
         return updateVisitSettlement(formData);
      },
   });
};

type ReceiptCreateParameters = {
   taxIdentifier: TaxIdentifier | undefined;
   visitSettlementId: number;
   medicalServices: VisitMedicalService[];
};

type VisitSettlementPaymentCreateReceiptActionRenderProps = {
   createReceipt: (taxIdentifier: TaxIdentifier | undefined) => Promise<VisitSettlement | null>;
};

type VisitSettlementPaymentCreateReceiptActionProps = {
   children: (renderProps: VisitSettlementPaymentCreateReceiptActionRenderProps) => JSX.Element;
   appointmentId: number;
   visitDate: Date;
   paymentMethod: PaymentMethod;
   paymentMethodDescription: string | null | undefined;
};

export const VisitSettlementPaymentCreateReceiptAction = (
   props: VisitSettlementPaymentCreateReceiptActionProps
): JSX.Element => {
   const { executeAsync: createReceipt } = useCqrsCommand<
      void,
      ReceiptCreateParameters,
      FiscalDocumentProviderAuthorizationFault | ExternalSystemBusinessFault
   >(
      (parameters) => {
         const command = new ReceiptCreateCommand();
         command.referenceId = parameters.visitSettlementId.toString();
         command.fiscalItems = mapVisitMedicalServiceToFiscalItems(parameters.medicalServices);
         command.sellDate = convertDateToLocalDate(props.visitDate);
         command.taxIdentifier = Choice.withUseDefaultIfNull(parameters.taxIdentifier ?? null);
         command.paymentMethod = mapFormBeanPaymentMethodToApiPaymentMethod(props.paymentMethod);
         command.paymentMethodDescription = Choice.withUseDefaultIfNull(props.paymentMethodDescription ?? null);

         return command;
      },
      {
         onFault: {
            'egabinet-invoice-frontend-api:FiscalDocumentProviderAuthorizationFault': () => {
               dispatchSnackbarNegativeEvent(
                  visitSettlementTranslationKeys.shared.commands.receiptCreateCommand.apiTokenFault
               );
            },
            'egabinet-invoice-frontend-api:ExternalSystemBusinessFault': ({ externalSystemErrorMessage }) => {
               dispatchSnackbarNegativeEvent(
                  createInterpolatedTranslationKey(
                     visitSettlementTranslationKeys.shared.commands.receiptCreateCommand.externalSystemBusinessFault,
                     {
                        messageFromFakturownia: externalSystemErrorMessage,
                     }
                  )
               );
            },
         },
      }
   );

   return props.children({
      createReceipt: async (taxIdentifier: TaxIdentifier | undefined) => {
         await refetchCqrsQuery(VisitSettlementQuery);
         const visitSettlement = await performVisitSettlementCqrsFind(props.appointmentId);

         if (visitSettlement && visitSettlement.medicalServices.length > 0) {
            await createReceipt({
               taxIdentifier: taxIdentifier,
               visitSettlementId: visitSettlement?.id,
               medicalServices: visitSettlement?.medicalServices,
            });
         }

         return visitSettlement;
      },
   });
};
